import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  ChangeDetectionStrategy,
  OnChanges,
  ElementRef,
  ViewChild,
  inject,
  OnInit,
  OnDestroy,
  TemplateRef,
  HostBinding,
  Input,
  ChangeDetectorRef,
  SimpleChanges,
  PLATFORM_ID,
} from '@angular/core';
import { TimeUtils } from '@freelancer/time-utils';
import {
  flChevronDown,
  flChevronUp,
  IconColor,
  IconName,
  IconSize,
  provideFlIcons,
} from '@freelancer/ui/icon';
import { LinkColor, LinkUnderline, LinkWeight } from '@freelancer/ui/link';
import { FontWeight, TextSize } from '@freelancer/ui/text';
import { isEqual } from '@freelancer/utils';
import { fromEvent, Subscription } from 'rxjs';
import {
  UserSearchCardAnnotation,
  UserSearchCardAnnotationActive,
  UserSearchCardAnnotationEmpty,
  UserSearchCardAnnotationSegment,
} from './user-search-card-annotation.types';

interface UserSearchCardAnnotationTextContext {
  readonly segments: readonly UserSearchCardAnnotationSegment[];
}

@Component({
  standalone: false,
  selector: 'fl-user-search-card-annotation',
  template: `
    @if (
      displayAnnotation.state === 'loading' ||
      displayAnnotation.state === 'active'
    ) {
      <div
        class="UserSearchCardAnnotation"
        flTrackingSection="UserSearchCardAnnotation"
        [class.Expanded]="expanded"
      >
        @switch (displayAnnotation.state) {
          @case ('loading') {
            @if (icon !== undefined) {
              <fl-icon
                class="Icon"
                [color]="IconColor.FOREGROUND"
                [name]="icon"
                [size]="IconSize.SMALL"
                [useIconFont]="true"
              ></fl-icon>
            }
            <fl-loading-text [rows]="1"> </fl-loading-text>
          }
          @case ('active') {
            <div
              class="TextContainer"
              [class.Gradient]="gradient"
              [class.mr-xxxsmall]="expandable"
            >
              @if (icon !== undefined) {
                <fl-icon
                  class="Icon"
                  [color]="IconColor.FOREGROUND"
                  [name]="icon"
                  [size]="IconSize.SMALL"
                  [useIconFont]="true"
                ></fl-icon>
              }
              <!--
                Duplicated the text into two <p> elements: collapsed, and expanded states
                  - collapsed:  shows the text in a limited number of lines (limited by maxLines)
                  - expanded:  allows the text to take as much height as needed to show all the content, without being limited by max-lines

                This is needed to measure the height of the text when it's collapsed vs expanded,
                to determine if the annotation is expandable (i.e. if the expanded text has a greater height than the collapsed one)

                Only one of them will be visible at a time (using CSS visibility, not display: none)
                Height is determined using clientHeight, which does not include the height of elements with display: none,
                but does include the height of elements with visibility: hidden
              -->
              @if (maxLines !== undefined && maxLines > 0) {
                <p
                  #collapsedText
                  class="Collapsed Text text-size-inherit"
                >
                  <ng-container
                    *ngTemplateOutlet="
                      text;
                      context: { segments: displayAnnotation.segments }
                    "
                  ></ng-container>
                </p>
              }
              <p
                #expandedText
                class="Expanded Text text-size-inherit"
              >
                <ng-container
                  *ngTemplateOutlet="
                    text;
                    context: { segments: displayAnnotation.segments }
                  "
                ></ng-container>
              </p>
            </div>
            <div
              class="Controls"
              [class.hide]="!expandable"
            >
              <fl-icon
                class="Expand"
                flTrackingLabel="Expand"
                [clickable]="true"
                [color]="IconColor.FOREGROUND"
                [name]="expanded ? 'flChevronUp' : 'flChevronDown'"
                [size]="IconSize.SMALL"
                [weight]="FontWeight.LIGHT"
                (click)="handleExpandClick()"
              ></fl-icon>
            </div>
          }
        }
      </div>
      <ng-template
        #text
        let-segments="segments"
      >
        @for (segment of segments; track trackBySegment($index, segment)) {
          @if (segment.type === 'link') {
            <fl-link
              class="Link"
              [color]="LinkColor.FOREGROUND"
              [weight]="LinkWeight.INHERIT"
              [flTrackingLabel]="'Link'"
              [flTrackingReferenceId]="segment.context.id"
              [flTrackingReferenceType]="segment.context.type"
              [link]="segment.link.path"
              [queryParams]="segment.link.queryParams"
              [newTab]="true"
              [size]="TextSize.INHERIT"
              [underline]="LinkUnderline.ALWAYS"
              (click)="$event.stopPropagation()"
              >{{ segment.text }}</fl-link
            >
          } @else {
            {{ segment.text }}
          }
        }
      </ng-template>
    }
  `,
  styleUrls: ['./user-search-card-annotation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideFlIcons({ flChevronDown, flChevronUp })],
})
export class UserSearchCardAnnotationComponent
  implements OnInit, OnChanges, OnDestroy
{
  private platformId = inject(PLATFORM_ID);
  private timeUtils = inject(TimeUtils);
  private changeDetectorRef = inject(ChangeDetectorRef);

  FontWeight = FontWeight;
  IconColor = IconColor;
  IconSize = IconSize;
  LinkColor = LinkColor;
  LinkWeight = LinkWeight;
  LinkUnderline = LinkUnderline;
  TextSize = TextSize;

  @ViewChild('collapsedText', { read: ElementRef })
  collapsedElement?: ElementRef<HTMLElement>;

  @ViewChild('expandedText', { read: ElementRef })
  expandedElement?: ElementRef<HTMLElement>;

  @ViewChild('text', { read: TemplateRef })
  textTemplate: TemplateRef<UserSearchCardAnnotationTextContext>;

  @Input({ required: true }) annotation: UserSearchCardAnnotation;
  @Input() gradient = true;
  @Input() icon: IconName = 'ui-sparkle';
  @Input() maxLines: number | undefined = undefined;

  @HostBinding('style.--max-lines')
  get cssMaxLines(): string {
    return this.maxLines !== undefined && this.maxLines > 0
      ? `${this.maxLines}`
      : 'unset';
  }

  expandable = false;
  expanded = false;

  displayAnnotation!: UserSearchCardAnnotation;

  private subscriptions = new Subscription();

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.subscriptions.add(
        fromEvent(globalThis, 'resize')
          .pipe(this.timeUtils.rxDebounceTime(100))
          .subscribe(() => {
            this.updateExpandable();
          }),
      );
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('annotation' in changes) {
      this.displayAnnotation = this.compressAnnotation(this.annotation);
    }

    // Check if expandable after the view has been updated (next tick) with the new annotation,
    // to ensure we are measuring the correct height (after the new annotation has been rendered)
    this.timeUtils.setTimeout(() => this.updateExpandable());
  }

  private compressAnnotation(
    annotation: UserSearchCardAnnotation,
  ): UserSearchCardAnnotation {
    switch (annotation.state) {
      case 'loading':
      case 'empty':
        // Nothing to compress, they have nothing but the state and empty segments array.
        return annotation;
      case 'active': {
        // Empty string segments can be removed as they have no effect in the UI
        const nonEmptySegments = annotation.segments.filter(
          segment => segment.text.length > 0,
        );
        const compressedSegments = this.compressSegments(nonEmptySegments);

        return this.createSegmentedAnnotation(compressedSegments);
      }
      default:
        throw new Error('Unknown annotation state');
    }
  }

  /**
   * Combine consecutive segments of the same type into a single segment, concatenating their text.
   * For link segments, they can only be combined if they have the same link and context.
   */
  private compressSegments(
    segments: readonly UserSearchCardAnnotationSegment[],
  ): readonly UserSearchCardAnnotationSegment[] {
    const compressedSegments: UserSearchCardAnnotationSegment[] = [];

    for (const segment of segments) {
      const previous = compressedSegments.pop();

      if (previous !== undefined && this.canBeMerged(previous, segment)) {
        compressedSegments.push({
          ...previous,
          text: previous.text + segment.text,
        });
      } else {
        if (previous !== undefined) {
          compressedSegments.push(previous);
        }
        compressedSegments.push(segment);
      }
    }

    return compressedSegments;
  }

  private updateExpandable(): void {
    if (this.maxLines === undefined) {
      this.expandable = false;
      this.expanded = true;
    } else {
      const collapsed = this.collapsedElement?.nativeElement;
      const expanded = this.expandedElement?.nativeElement;

      if (collapsed === undefined || expanded === undefined) {
        this.expandable = false;
        this.expanded = true;
      } else {
        this.expandable = expanded.clientHeight > collapsed.clientHeight;
      }
    }

    // This method can be called asynchronously,
    // so we need to mark it for check to ensure the view is updated with the new expandable value
    this.changeDetectorRef.markForCheck();
  }

  handleExpandClick(): void {
    this.expanded = !this.expanded;
  }

  private canBeMerged<
    A extends UserSearchCardAnnotationSegment,
    B extends UserSearchCardAnnotationSegment,
  >(a: A, b: B): b is B & { type: A['type'] } {
    if (a.type !== b.type) {
      return false;
    }

    switch (a.type) {
      case 'text':
        // text segments can be merged regardless of their content
        // nothing special with text
        return true;
      case 'link':
        if (a.type !== b.type) {
          // Should not happen due the condition at the start, just to satisfy typescript
          return false;
        }

        // If it points to the same link, then should be fine to merge,
        // However, if the context is different, don't merge it as the context is used in flTracking
        return a.link === b.link && isEqual(a.context, b.context);
      default:
        throw new Error('Unknown segment type');
    }
  }

  /**
   * Create a new annotation with the given segments,
   *  - 'active': if there is at least one segment,
   *  - 'empty' : if there are no segments.
   */
  private createSegmentedAnnotation(
    segments: readonly UserSearchCardAnnotationSegment[],
  ): UserSearchCardAnnotationActive | UserSearchCardAnnotationEmpty {
    if (segments.length === 0) {
      return {
        state: 'empty',
        segments: [],
      };
    }

    return {
      state: 'active',
      segments: [segments[0], ...segments.slice(1)],
    };
  }

  trackBySegment(
    index: number,
    segment: UserSearchCardAnnotationSegment,
  ): string {
    switch (segment.type) {
      case 'text':
        return `${index}`;
      case 'link': {
        const url = `${segment.link.path}?${new URLSearchParams(segment.link.queryParams ?? {}).toString()}`;

        // These are the only properties passed to <fl-link>
        return `${index},${url},${segment.context.id}:${segment.context.type}`;
      }
      default:
        throw new Error('Unknown segment type');
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
